import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Heart, Moon, Baby, Thermometer, Dumbbell } from "lucide-react";

const WomenWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const womenTab = tabs.find((tab) => tab.id === "women");
    const widget = womenTab?.widget || {};

    return (
        <motion.div className="relative w-full" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.5 }}>
            {/* Main widget */}
            <div className="bg-gradient-to-br from-white via-white to-pink-50 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-pink-100 hover:shadow-pink-200/50 transition-all duration-300">
                <div className="flex justify-between items-center mb-3">
                    <h5 className="text-pink-800 font-medium flex items-center">
                        <motion.div className={`inline-block ${isRTL ? "ml-2" : "mr-2"} text-pink-500`} animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}>
                            <Heart className="w-5 h-5 fill-current" />
                        </motion.div>
                        {widget.title}
                    </h5>
                    <motion.div
                        className="flex items-center bg-gradient-to-r from-pink-500 to-rose-600 text-white px-3 py-1.5 rounded-full shadow-sm"
                        whileHover={{ scale: 1.05, boxShadow: "0 3px 10px rgba(236, 72, 153, 0.3)" }}
                    >
                        <motion.div className={`inline-block ${isRTL ? "ml-1" : "mr-1"}`} transition={{ duration: 8, repeat: Infinity, ease: "linear" }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                            </svg>
                        </motion.div>
                        <span className="text-sm font-medium">{widget.cycleDay}</span>
                    </motion.div>
                </div>

                {/* Cycle display */}
                <div className="relative h-36 mb-3 overflow-hidden rounded-lg bg-gradient-to-br from-pink-50 to-rose-50 flex items-center justify-center group">
                    <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-pink-400/10 to-rose-500/10 opacity-20 group-hover:opacity-30 transition-opacity duration-500"
                        animate={{ scale: [1, 1.02, 1] }}
                        transition={{ duration: 4, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
                    />

                    {/* Circular cycle display */}
                    <div className="relative flex items-center justify-center w-full h-full">
                        {/* Outer circle */}
                        <motion.div
                            className="absolute w-28 h-28 rounded-full border-4 border-pink-200"
                            animate={{
                                boxShadow: ["0 0 0px rgba(236, 72, 153, 0.1)", "0 0 20px rgba(236, 72, 153, 0.3)", "0 0 0px rgba(236, 72, 153, 0.1)"],
                            }}
                            transition={{ duration: 3, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
                        />

                        {/* Middle circle with progress */}
                        <motion.div
                            className="absolute w-20 h-20 rounded-full bg-gradient-to-br from-pink-400/20 to-rose-400/20 flex items-center justify-center"
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 2.5, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
                        >
                            {/* Progress circle */}
                            <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="35" stroke="#f3f4f6" strokeWidth="6" fill="none" />
                                <motion.circle
                                    cx="50"
                                    cy="50"
                                    r="35"
                                    stroke="#ec4899"
                                    strokeWidth="6"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeDasharray={`${2 * Math.PI * 35}`}
                                    initial={{ strokeDashoffset: 2 * Math.PI * 35 }}
                                    animate={{ strokeDashoffset: 2 * Math.PI * 35 * (1 - 0.43) }} // Day 12 of 28
                                    transition={{ duration: 2, ease: "easeOut" }}
                                />
                            </svg>

                            {/* Center text */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <motion.span className="text-lg font-bold text-pink-600" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1, duration: 0.5 }}>
                                    {widget.currentDay}
                                </motion.span>
                                <span className="text-xs text-gray-500">{widget.cycleLabel}</span>
                            </div>
                        </motion.div>
                    </div>

                    {/* Current status */}
                    <div className="absolute bottom-2 text-center">
                        <motion.p className="text-sm font-medium text-pink-700" animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity }}>
                            {widget.phase}
                        </motion.p>
                        <p className="text-xs text-gray-500 mt-1">{widget.nextPeriod}</p>
                    </div>
                </div>

                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                    <motion.div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-lg p-2 text-center" whileHover={{ scale: 1.05 }}>
                        <p className="text-lg font-bold text-pink-600">{widget.cycleLength?.value}</p>
                        <p className="text-xs text-gray-600">{widget.cycleLength?.label}</p>
                    </motion.div>
                    <motion.div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-2 text-center" whileHover={{ scale: 1.05 }}>
                        <p className="text-lg font-bold text-purple-600">{widget.periodDays?.value}</p>
                        <p className="text-xs text-gray-600">{widget.periodDays?.label}</p>
                    </motion.div>
                    <motion.div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-2 text-center" whileHover={{ scale: 1.05 }}>
                        <p className="text-lg font-bold text-orange-600">{widget.bodyTemp?.value}</p>
                        <p className="text-xs text-gray-600">{widget.bodyTemp?.label}</p>
                    </motion.div>
                </div>
            </div>

            {/* Sub-widgets */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {/* Symptoms and feelings */}
                <motion.div
                    className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-xl p-3 border border-pink-50 shadow-sm"
                    whileHover={{ scale: 1.03, boxShadow: "0 4px 12px rgba(236, 72, 153, 0.15)" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-pink-700 font-medium">{widget.symptoms?.title}</span>
                        <motion.div
                            className="w-6 h-6 rounded-full bg-pink-100 flex items-center justify-center"
                            animate={{ rotate: [0, 10, -10, 0] }}
                            transition={{ duration: 4, repeat: Infinity, repeatDelay: 2 }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="#EC4899" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                        </motion.div>
                    </div>
                    <div className="flex flex-col">
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-xs text-gray-500">{widget.symptoms?.overall?.label}</span>
                            <span className="text-xs bg-green-50 text-green-600 px-1.5 py-0.5 rounded-md">{widget.symptoms?.overall?.value}</span>
                        </div>
                        {/* Symptoms list */}
                        <div className="mt-1 space-y-2">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-600">{widget.symptoms?.energy?.label}</span>
                                <div className="flex space-x-1">
                                    {[1, 2, 3, 4].map((_, index) => (
                                        <div key={index} className="w-2 h-2 rounded-full bg-pink-400"></div>
                                    ))}
                                    <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                                </div>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-600">{widget.symptoms?.mood?.label}</span>
                                <span className="text-pink-600">{widget.symptoms?.mood?.value}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-600">{widget.symptoms?.pain?.label}</span>
                                <span className="text-green-600">{widget.symptoms?.pain?.value}</span>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Predictions and reminders */}
                <motion.div
                    className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-xl p-3 border border-purple-50 shadow-sm"
                    whileHover={{ scale: 1.03, boxShadow: "0 4px 12px rgba(147, 51, 234, 0.15)" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-purple-700 font-medium">{widget.predictions?.title}</span>
                        <motion.div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center" animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 0.8, repeat: Infinity }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="#9333EA" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
                            </svg>
                        </motion.div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-gray-500">{widget.predictions?.nextPeriod?.label}</span>
                            <span className="text-xs bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded-md">{widget.predictions?.nextPeriod?.value}</span>
                        </div>
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-xs text-gray-500">{widget.predictions?.fertile?.label}</span>
                            <span className="text-xs bg-pink-50 text-pink-600 px-1.5 py-0.5 rounded-md">{widget.predictions?.fertile?.value}</span>
                        </div>
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-xs text-gray-500">{widget.predictions?.ovulation?.label}</span>
                            <span className="text-xs bg-green-50 text-green-600 px-1.5 py-0.5 rounded-md">{widget.predictions?.ovulation?.value}</span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default WomenWidget;
